import React, { useCallback, useRef, useContext } from 'react';

import type { BannerStateContainer } from '@confluence/banners';
import {
	ExperienceTrackerContext,
	DATABASE_CONTENT_EXPERIENCE,
} from '@confluence/experience-tracker';

import { DATABASE_IFRAME_TITLE } from '../database-constants';
import { DATABASE_VIEW_PAGE_CONTENT_SEGMENT } from '../perf.config';
import { databaseShellStyle } from '../database-shell-styles';
import { useDatabaseBridgeData } from '../hooks/useDatabaseBridgeData';
import { useDatabaseBridgeMethods } from '../hooks/useDatabaseBridgeMethods';
import { usePostMessageHandler, useOnboardingService } from '../hooks';
import type { DatabaseResolvedCommentsService } from '../services/database-resolved-comment-service';
import { useDatabaseParams } from '../database-params';
import type { DatabaseIdentity } from '../database-shell-types';

interface DatabaseIframeProps {
	contentId: string;
	contentReferenceId?: string | null;
	spaceKey?: string;
	dataSecurityPolicyPageExport: boolean | null;
	identity?: DatabaseIdentity | null;
	refetch?: () => void;
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	loading: boolean;
	isSmartLinkEmbed: boolean;
	headerHeight: number;
	bannerState: BannerStateContainer;
	resolvedCommentService: DatabaseResolvedCommentsService;
}

export const DatabaseIframe = ({
	contentId,
	contentReferenceId,
	spaceKey,
	dataSecurityPolicyPageExport,
	identity,
	refetch,
	spaceId,
	spaceName,
	title,
	loading,
	isSmartLinkEmbed,
	headerHeight,
	bannerState,
	resolvedCommentService,
}: DatabaseIframeProps) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	usePostMessageHandler(iframeRef);
	const onboardingService = useOnboardingService();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const params = useDatabaseParams();

	const { initialUrl, bridgeData } = useDatabaseBridgeData({
		contentId,
		contentReferenceId,
		spaceKey,
		dataSecurityPolicyPageExport,
		identity,
		refetch,
		spaceId,
		spaceName,
		title,
		version: params['databases-frontend'],
		isSmartLinkEmbed,
	});

	const bridgeMethods = useDatabaseBridgeMethods({
		experienceTracker,
		onboardingService,
		resolvedCommentService,
		segmentMetric: DATABASE_VIEW_PAGE_CONTENT_SEGMENT,
		experienceName: DATABASE_CONTENT_EXPERIENCE,
	});

	usePostMessageHandler(iframeRef);

	const onIframeLoaded = useCallback(
		(e: React.SyntheticEvent<HTMLIFrameElement>) => {
			const iframe = e.target as HTMLIFrameElement;
			if (iframe) {
				iframeRef.current = iframe;
				if (iframe.contentWindow) {
					iframe.contentWindow.databaseBridge = bridgeMethods;

					iframe.contentWindow.postMessage(
						{
							type: 'bridge-ready',
						},
						'*',
					);
				}
			}
		},
		[bridgeMethods],
	);

	return (
		<>
			<iframe
				src={initialUrl}
				title={DATABASE_IFRAME_TITLE}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				style={databaseShellStyle(
					!isSmartLinkEmbed ? bannerState.getTotalHeight(true) + headerHeight : 0,
				)}
				data-bridge-data={!loading ? JSON.stringify(bridgeData) : undefined}
				onLoad={onIframeLoaded}
			/>
		</>
	);
};
